import type { SearchResults } from '#shared/types/search'
import { rankJobRecommendations, type JobRecommendationSignals } from '#shared/utils/jobRecommendation'
import { JOBS_NEWEST_FIRST_SORT } from '#shared/utils/jobSearchDocument'
import { useTypesense } from '../typesense/useTypesense'

// Typesense returns at most 250 hits per request. All live jobs fit (about 60 in 11/2026), so the
// recommendations are ranked as one list and paginated here, and every page is consistent.
const MAX_RECOMMENDATION_CANDIDATES = 250

/**
 * Composable for the /praca listing: newest first, or recommendations for a signed-in person
 */
export function useSemanticJobSearch() {
    const { semanticSearch, hybridSearch, keywordSearch } = useTypesense()

    /**
     * Without a personalization query: live jobs, newest first (or by `sort_by`).
     * With one: "Odporúčané", see shared/utils/jobRecommendation.ts. Only the embedding is used:
     * keyword matching of a whole profile text hit random jobs and pushed them to the top.
     */
    const searchJobsSemanticaly = async (
        query: string,
        options: {
            page?: number
            hitsPerPage?: number
            filters?: string
            filterBy?: string
            ruleContexts?: string[]
            sort_by?: string
            /** Text of the person's profile, see buildJobRecommendationQuery. */
            personalizationQuery?: string
            /** Seniority and preferences from the profile, see getJobRecommendationSignals. */
            recommendationSignals?: JobRecommendationSignals
        } = {}
    ): Promise<SearchResults> => {
        const now = Date.now()
        const filterBy = [`visibility:=published && endDate:>${now}`, options.filterBy || options.filters]
            .filter(Boolean)
            .join(' && ')
        const page = options.page || 0
        const hitsPerPage = options.hitsPerPage || 24
        const trimmedQuery = typeof query === 'string' ? query.trim() : ''

        if (!trimmedQuery && options.personalizationQuery && !options.sort_by) {
            try {
                const candidates = await hybridSearch('jobs', options.personalizationQuery, {
                    page: 0,
                    per_page: MAX_RECOMMENDATION_CANDIDATES,
                    filterBy,
                    queryBy: 'embedding',
                    queryByWeights: '1'
                })
                const signals = options.recommendationSignals ?? {
                    jobType: [],
                    formOfCollaboration: [],
                    jobPlace: [],
                    jobLocation: []
                }
                const ranked = rankJobRecommendations(
                    candidates.hits,
                    (job: any) => job._typesense?.vector_distance,
                    signals,
                    now
                )

                return {
                    ...candidates,
                    hits: ranked.slice(page * hitsPerPage, (page + 1) * hitsPerPage),
                    nbHits: ranked.length,
                    page,
                    nbPages: Math.ceil(ranked.length / hitsPerPage),
                    hitsPerPage
                }
            } catch (error) {
                // The listing must not go empty because of the recommendations: fall back to newest
                console.error('Error in job recommendations, showing newest jobs:', error)
            }
        }

        return await keywordSearch('jobs', trimmedQuery || '*', {
            page,
            per_page: hitsPerPage,
            filterBy,
            sort_by: options.sort_by || JOBS_NEWEST_FIRST_SORT
        })
    }

    /**
     * Search jobs with pure semantic search (no keyword matching)
     */
    const searchJobsPureSemantic = async (
        query: string,
        options: {
            page?: number
            hitsPerPage?: number
            filters?: string
            filterBy?: string
        } = {}
    ): Promise<SearchResults> => {
        try {
            const trimmedQuery = typeof query === 'string' ? query.trim() : ''
            if (!trimmedQuery) {
                throw new Error('Search query is required')
            }

            // Filter by visibility AND endDate > now (exclude expired jobs)
            const now = Date.now()
            let filter = `visibility:=published && endDate:>${now}`
            if (options.filterBy || options.filters) {
                filter += ` && ${options.filterBy || options.filters}`
            }

            return await semanticSearch('jobs', trimmedQuery, {
                hitsPerPage: options.hitsPerPage || 25,
                page: options.page,
                filterBy: filter
            })
        } catch (error: any) {
            console.error('Error in pure semantic job search:', error)
            throw new Error(error.message || 'Failed to search jobs')
        }
    }

    return {
        searchJobsSemanticaly,
        searchJobsPureSemantic
    }
}
